import parser from 'node-html-parser';
import sanitizeHtml from 'sanitize-html';
import { match } from 'ts-pattern';

import { BuilderString } from './types';

function toBuilderText(input: unknown): string {
  return typeof input === 'string' ? input : '';
}

function getLocalizedBuilderText(
  builderString: BuilderString,
  locale: string
): string {
  if (typeof builderString === 'string') {
    return builderString;
  }

  const localizedValue = builderString[locale];
  if (typeof localizedValue === 'string') {
    return localizedValue;
  }

  const englishValue = builderString.en;
  if (typeof englishValue === 'string' && englishValue.length > 0) {
    return englishValue;
  }

  const defaultValue = builderString.Default;
  if (typeof defaultValue === 'string') {
    return defaultValue;
  }

  return '';
}

const DEENTITIZE_MAP: { [key in string]: string } = {
  '&gt;': '>',
  '&lt;': '<',
  '&amp;': '&',
  '&apos;': "'",
  '&quot;': '"',
};
function deentitize(text: string) {
  return Object.keys(DEENTITIZE_MAP).reduce(
    (result, key) => result.replace(new RegExp(key, 'g'), DEENTITIZE_MAP[key]),
    text
  );
}

export function cleanBuilderStringInput(
  input: unknown,
  textOnly = false
): string {
  let sanitizedHtml;
  const builderText = toBuilderText(input);

  if (textOnly) {
    sanitizedHtml = sanitizeHtml(builderText, { allowedTags: [] });
  } else {
    sanitizedHtml = sanitizeHtml(builderText, {
      allowedTags: ['span', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      allowedAttributes: {
        span: ['class'],
        a: ['href', 'class', 'rel', 'target'],
        h1: ['class'],
        h2: ['class'],
        h3: ['class'],
        h4: ['class'],
        h5: ['class'],
        h6: ['class'],
      },
    });
  }

  return deentitize(sanitizedHtml);
}

export function cleanBuilderParagraphInput(input: unknown): string {
  return deentitize(
    sanitizeHtml(toBuilderText(input), {
      allowedTags: ['span', 'a', 'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      allowedAttributes: {
        span: ['class'],
        a: ['href', 'class', 'rel', 'target'],
        p: ['class'],
        h1: ['class'],
        h2: ['class'],
        h3: ['class'],
        h4: ['class'],
        h5: ['class'],
        h6: ['class'],
      },
    })
  );
}

export function getBuilderString(
  builderString: BuilderString | undefined,
  locale: string
): string {
  if (!builderString) return '';

  return getLocalizedBuilderText(builderString, locale);
}

export enum SanitizeMode {
  SINGLE_LINE_TEXT_ONLY,
  SINGLE_LINE,
  MULTIPLE_LINE,
}
export function getCleanInput(
  locale: string,
  mode: SanitizeMode,
  input?: BuilderString
) {
  const string = getBuilderString(input, locale);
  return match(mode)
    .with(SanitizeMode.SINGLE_LINE_TEXT_ONLY, () =>
      cleanBuilderStringInput(string, true)
    )
    .with(SanitizeMode.SINGLE_LINE, () =>
      cleanBuilderStringInput(string, false)
    )
    .otherwise(() => cleanBuilderParagraphInput(string));
}

export function isBuilderStringPresent(
  builderString: BuilderString | undefined,
  locale: string
) {
  if (!builderString) return false;

  const input = getBuilderString(builderString, locale);
  const dom = parser.parse(`<div id="root">${input}</div>`);
  const text = dom.getElementById('root')?.textContent?.trim();
  return Boolean(text?.length);
}

export function someBuilderStringPresent(
  builderStrings: (BuilderString | undefined)[],
  locale: string
) {
  return builderStrings
    .map((v) => isBuilderStringPresent(v, locale))
    .some((v) => v);
}
